import React from 'react';
import { motion } from 'framer-motion';
import { Compass, PieChart, Network, Lock, ArrowUpRight, Sparkles } from 'lucide-react';
import { SectionHeader } from './HowItWorksSection';
import AnimatedNumber from './AnimatedNumber';

const benefits = [
  { icon: Compass, title: 'Descubra negócios incríveis', desc: 'Conheça pessoas com ideias reais que precisam de uma chance para crescer.' },
  { icon: PieChart, title: 'Comece com pouco', desc: 'Você pode investir a partir de R$ 1.000 e fazer parte da história de várias ideias.' },
  { icon: Network, title: 'Faça parte de uma rede', desc: 'Encontre outros investidores e empreendedores apaixonados pelo que fazem.' },
  { icon: Lock, title: 'Oportunidades que ninguém vê', desc: 'Acesso antecipado a pequenos negócios cheios de potencial.' },
];

const ForInvestorsSection = () => {
  return (
    <section id="investors" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="Para quem quer investir"
          title={<>Apoie ideias que <span className="gradient-text-blue">acreditam no futuro</span>.</>}
          subtitle="Pequenos negócios podem virar grandes histórias. Aqui você descobre, escolhe e investe nas que mais combinam com você."
        />

        <div className="mt-16 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left — benefits */}
          <div className="grid sm:grid-cols-2 gap-4">
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                className="glass-card-blue rounded-2xl p-5 card-hover spotlight-card"
              >
                <div className="w-10 h-10 rounded-xl bg-[#4facfe]/15 text-[#4facfe] flex items-center justify-center mb-4">
                  <b.icon size={18} />
                </div>
                <div className="text-base font-semibold text-white">{b.title}</div>
                <p className="mt-2 text-xs text-slate-400 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Right — Dashboard mockup */}
          <InvestorDashboard />
        </div>
      </div>
    </section>
  );
};

const InvestorDashboard = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.8 }}
      className="relative"
    >
      {/* Glow */}
      <div className="absolute -inset-4 bg-gradient-to-br from-[#4facfe]/20 to-[#1e90ff]/10 rounded-3xl blur-2xl" />

      <div className="relative glass-card rounded-3xl p-5 sm:p-6 glow-blue">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 text-[10px] text-slate-400 tracking-widest uppercase">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Seus investimentos
            </div>
            <div className="mt-1 text-xl sm:text-2xl heading-display font-bold gradient-text-blue">
              R$ <AnimatedNumber end={847250} />
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-green-400 bg-green-400/10 rounded-full px-3 py-1.5">
            <ArrowUpRight size={12} />
            +<AnimatedNumber end={42.7} decimals={1} />%
          </div>
        </div>

        {/* Chart */}
        <div className="glass-card rounded-2xl p-4 mb-4">
          <div className="text-[10px] text-slate-400 tracking-widest uppercase mb-2">Crescimento nos últimos 6 meses</div>
          <MiniChart />
          <div className="flex justify-between mt-2 text-[10px] text-slate-500">
            {['Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out'].map(m => <span key={m}>{m}</span>)}
          </div>
        </div>

        {/* Holdings */}
        <div className="space-y-2.5">
          {[
            { name: 'AgroTech Brasil', amount: 'R$ 250.000', change: '+18.4%', color: 'green' },
            { name: 'EcoModa SP', amount: 'R$ 180.500', change: '+12.1%', color: 'green' },
            { name: 'HealthSpark', amount: 'R$ 416.750', change: '+8.9%', color: 'blue' },
          ].map((h, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
              className="flex items-center justify-between glass-card rounded-xl px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4facfe]/30 to-[#ffa726]/20 flex items-center justify-center text-[10px] font-bold">
                  {h.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                </div>
                <div>
                  <div className="text-sm font-medium text-white leading-tight">{h.name}</div>
                  <div className="text-[10px] text-slate-500">{h.amount}</div>
                </div>
              </div>
              <div className={`text-xs font-semibold ${h.color === 'green' ? 'text-green-400' : 'text-[#4facfe]'}`}>
                {h.change}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tag */}
        <div className="absolute -top-3 -right-3 glass-card-gold rounded-full px-3 py-1.5 text-[10px] font-semibold text-[#ffd54f] flex items-center gap-1.5 glow-gold float-med">
          <Sparkles size={11} />
          Recomendado pra você
        </div>
      </div>
    </motion.div>
  );
};

const MiniChart = () => {
  const points = [20, 35, 28, 50, 45, 70, 65, 85, 78, 95];
  const max = Math.max(...points);
  const w = 100;
  const h = 50;
  const step = w / (points.length - 1);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${i * step} ${h - (p / max) * h}`)
    .join(' ');
  const area = `${path} L ${w} ${h} L 0 ${h} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" preserveAspectRatio="none">
      <defs>
        <linearGradient id="area-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4facfe" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#4facfe" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#4facfe" />
          <stop offset="100%" stopColor="#ffa726" />
        </linearGradient>
      </defs>
      <motion.path
        d={area}
        fill="url(#area-grad)"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.5 }}
      />
      <motion.path
        d={path}
        fill="none"
        stroke="url(#line-grad)"
        strokeWidth="1.2"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: 'easeOut' }}
      />
    </svg>
  );
};

export default ForInvestorsSection;
